/**
 * Creator Chat TTS playback helpers.
 *
 * Browsers block HTMLAudioElement.play() after an async LLM wait, even if Send
 * was a user gesture. Resuming AudioContext during the gesture and playing via
 * decodeAudioData usually works. A shared HTMLAudio used for "silent unlock"
 * must never be paused after TTS starts — that race kills the reply voice.
 *
 * New reply / proactive audio is queued so speech is never cut off mid-sentence.
 */

import { getApiBaseUrl } from '../utils/apiConfig';

const SILENT_WAV =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';

export type CreatorChatAudioRefs = {
  ctxRef: { current: AudioContext | null };
  htmlAudioRef: { current: HTMLAudioElement | null };
  sourceRef: { current: AudioBufferSourceNode | null };
  generationRef: { current: number };
  /** URLs waiting to play after the current clip finishes. */
  queueRef?: { current: string[] };
  /** True while a clip is actively playing (or starting). */
  playingRef?: { current: boolean };
  /** Resolve waiters when playback becomes idle. */
  idleWaitersRef?: { current: Array<() => void> };
};

function ensureQueue(refs: CreatorChatAudioRefs): string[] {
  if (!refs.queueRef) {
    refs.queueRef = { current: [] };
  }
  return refs.queueRef.current;
}

function ensurePlayingRef(refs: CreatorChatAudioRefs): { current: boolean } {
  if (!refs.playingRef) {
    refs.playingRef = { current: false };
  }
  return refs.playingRef;
}

function ensureIdleWaiters(refs: CreatorChatAudioRefs): Array<() => void> {
  if (!refs.idleWaitersRef) {
    refs.idleWaitersRef = { current: [] };
  }
  return refs.idleWaitersRef.current;
}

function notifyIdle(refs: CreatorChatAudioRefs): void {
  const waiters = ensureIdleWaiters(refs).splice(0);
  waiters.forEach((resolve) => {
    try {
      resolve();
    } catch {
      /* ignore */
    }
  });
}

function getAudioContextCtor(): typeof AudioContext | null {
  const w = window as Window & { webkitAudioContext?: typeof AudioContext };
  return window.AudioContext || w.webkitAudioContext || null;
}

/** Prefer API-host /uploads paths so fetch() gets CORS from the Java API. */
function candidateFetchUrls(url: string): string[] {
  const out: string[] = [];
  const push = (u: string) => {
    if (u && !out.includes(u)) out.push(u);
  };
  push(url);
  try {
    const parsed = new URL(url, window.location.href);
    const marker = '/uploads/';
    const idx = parsed.pathname.indexOf(marker);
    if (idx >= 0) {
      const path = parsed.pathname.slice(idx) + parsed.search;
      const api = (getApiBaseUrl() || '').replace(/\/+$/, '');
      if (api && !api.startsWith('/')) {
        push(api + path);
      }
      push(path);
    }
  } catch {
    /* ignore */
  }
  return out;
}

function isTouchMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(max-width: 768px)').matches &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );
}

async function fetchTtsArrayBuffer(url: string): Promise<ArrayBuffer> {
  let lastError: unknown;
  for (const candidate of candidateFetchUrls(url)) {
    try {
      const res = await fetch(candidate, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
      if (!res.ok) {
        lastError = new Error(`TTS fetch ${res.status}`);
        continue;
      }
      return await res.arrayBuffer();
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError || new Error('TTS fetch failed');
}

/** Resume AudioContext + prime autoplay with a throwaway element (never touch TTS element). */
export function unlockCreatorChatAudioGesture(refs: CreatorChatAudioRefs): void {
  try {
    const AC = getAudioContextCtor();
    if (AC) {
      if (!refs.ctxRef.current) {
        refs.ctxRef.current = new AC();
      }
      void refs.ctxRef.current.resume();
    }
    const primer = new Audio(SILENT_WAV);
    void primer.play().then(() => {
      primer.pause();
    }).catch(() => undefined);
  } catch {
    /* ignore */
  }
}

export function isCreatorChatAudioBusy(refs: CreatorChatAudioRefs): boolean {
  if (ensurePlayingRef(refs).current) return true;
  const queue = refs.queueRef?.current;
  return !!(queue && queue.length > 0);
}

/** Resolves when nothing is playing and the queue is empty. */
export function waitForCreatorChatAudioIdle(refs: CreatorChatAudioRefs): Promise<void> {
  if (!isCreatorChatAudioBusy(refs)) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    ensureIdleWaiters(refs).push(resolve);
  });
}

function hardStopCurrent(refs: CreatorChatAudioRefs): void {
  refs.generationRef.current += 1;
  const source = refs.sourceRef.current;
  refs.sourceRef.current = null;
  if (source) {
    try {
      source.stop();
    } catch {
      /* already stopped */
    }
  }
  const html = refs.htmlAudioRef.current;
  if (html) {
    try {
      html.onended = null;
      html.pause();
      html.removeAttribute('src');
      html.load();
    } catch {
      /* ignore */
    }
  }
  ensurePlayingRef(refs).current = false;
}

export function stopCreatorChatAudio(refs: CreatorChatAudioRefs): void {
  if (refs.queueRef) {
    refs.queueRef.current = [];
  }
  hardStopCurrent(refs);
  notifyIdle(refs);
}

function onClipFinished(refs: CreatorChatAudioRefs, generation: number): void {
  if (generation !== refs.generationRef.current) return;
  ensurePlayingRef(refs).current = false;
  const queue = ensureQueue(refs);
  const next = queue.shift();
  if (next) {
    void playNow(next, refs);
    return;
  }
  notifyIdle(refs);
}

async function playViaHtmlBlob(
  raw: ArrayBuffer,
  refs: CreatorChatAudioRefs,
  generation: number
): Promise<boolean> {
  try {
    const blob = new Blob([raw], { type: 'audio/mpeg' });
    const blobUrl = URL.createObjectURL(blob);
    const audio = refs.htmlAudioRef.current || new Audio();
    refs.htmlAudioRef.current = audio;
    audio.pause();
    audio.src = blobUrl;
    audio.currentTime = 0;
    audio.onended = () => {
      URL.revokeObjectURL(blobUrl);
      onClipFinished(refs, generation);
    };
    await audio.play();
    return generation === refs.generationRef.current;
  } catch {
    return false;
  }
}

async function playNow(url: string, refs: CreatorChatAudioRefs): Promise<boolean> {
  if (!url) return false;
  const generation = ++refs.generationRef.current;
  ensurePlayingRef(refs).current = true;

  // Stop only the active clip — queued items stay.
  const prev = refs.sourceRef.current;
  refs.sourceRef.current = null;
  if (prev) {
    try {
      prev.stop();
    } catch {
      /* ignore */
    }
  }
  if (refs.htmlAudioRef.current) {
    try {
      refs.htmlAudioRef.current.onended = null;
      refs.htmlAudioRef.current.pause();
    } catch {
      /* ignore */
    }
  }

  let raw: ArrayBuffer;
  try {
    raw = await fetchTtsArrayBuffer(url);
  } catch {
    onClipFinished(refs, generation);
    return false;
  }
  if (generation !== refs.generationRef.current) return true;

  if (isTouchMobileDevice()) {
    const mobileOk = await playViaHtmlBlob(raw, refs, generation);
    if (mobileOk) return true;
    onClipFinished(refs, generation);
    return false;
  }

  const AC = getAudioContextCtor();
  if (AC) {
    try {
      if (!refs.ctxRef.current) {
        refs.ctxRef.current = new AC();
      }
      const ctx = refs.ctxRef.current;
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      const decoded = await ctx.decodeAudioData(raw.slice(0));
      if (generation !== refs.generationRef.current) return true;
      const source = ctx.createBufferSource();
      source.buffer = decoded;
      source.connect(ctx.destination);
      refs.sourceRef.current = source;
      source.onended = () => {
        if (refs.sourceRef.current === source) {
          refs.sourceRef.current = null;
        }
        onClipFinished(refs, generation);
      };
      source.start(0);
      return true;
    } catch {
      // fall through
    }
  }

  if (generation !== refs.generationRef.current) return true;

  const ok = await playViaHtmlBlob(raw, refs, generation);
  if (!ok) {
    onClipFinished(refs, generation);
  }
  return ok;
}

/**
 * Queue a TTS URL. Plays immediately if idle; otherwise waits until the current
 * (and any earlier queued) clips finish. Never cuts off speech mid-sentence.
 */
export async function playCreatorChatAudioUrl(
  url: string,
  refs: CreatorChatAudioRefs
): Promise<boolean> {
  if (!url) return false;
  const playing = ensurePlayingRef(refs);
  const queue = ensureQueue(refs);

  if (playing.current || queue.length > 0) {
    queue.push(url);
    return true;
  }

  return playNow(url, refs);
}
